import App from 'next/app'
import React from 'react'
import { ApolloProvider } from '@apollo/client'
import Cookies from 'js-cookie'
import '@fortawesome/fontawesome-svg-core/styles.css'

import withApolloClient from 'lib/WithApolloClient'
import { siteDomain, siteHost } from 'utils/CareguideDomains'
import getSentry from 'utils/Sentry'
import SentryErrorReportingPage from 'components/SentryErrorReportingPage'
import { getEnvironment } from 'utils/Environments'
import CareGuideCoreApplication from 'components/CareGuideCoreApplication'
import {
  getRedirectMiddleware,
  handleNearRedirect,
  handleInvalidGetStartedStepRedirect,
  handle404Redirect,
  handleAutoLoginRedirect,
} from 'lib/serverSideRedirects'

const { captureException } = getSentry()

class MyApp extends App {
  state = {
    errorEventId: undefined,
  }

  static async getInitialProps(ctx) {
    const nextContext = ctx?.ctx
    try {
      const runRedirectMiddleware = getRedirectMiddleware([
        handleAutoLoginRedirect,
        handleNearRedirect,
        handleInvalidGetStartedStepRedirect,
        handle404Redirect,
      ])
      const isServerSideRedirect = await runRedirectMiddleware(nextContext)
      const pageProps = {
        domain: siteDomain(nextContext),
        host: siteHost(nextContext),
        environment: getEnvironment(nextContext),
        cloudflareForwardedIp: nextContext?.req?.headers['cf-connecting-ip'],
        directoriesCanonicalUrl: this.directoriesCanonicalUrl(nextContext),
        isServerSideRedirect,
      }
      return { pageProps }
    } catch (error) {
      console.log(error)
      // Capture errors that happen during a page's getInitialProps; work both client and server-side.
      const errorEventId = captureException(error, nextContext)
      return {
        hasError: true,
        errorEventId,
      }
    }
  }

  static directoriesCanonicalUrl(nextContext) {
    const host = siteHost(nextContext)
    if (nextContext.req && nextContext.req.url) {
      return `https://${host}${nextContext.req.url}`
    }

    return null
  }

  // Allows for rendering the Sentry report error UI as a fallback.
  static getDerivedStateFromError() {
    return { hasError: true }
  }

  /**
   *
   * @description Catch and log capture client-side exceptions to Sentry
   * @param {*} error
   * @param {Object} errorInfo
   */
  async componentDidCatch(error, errorInfo) {
    const errorEventId = captureException(error, { errorInfo })
    // Store error in state; used to conditionally render Sentry error reporting UI.
    this.setState({ errorEventId })
    super.componentDidCatch(error, errorInfo)
  }

  render() {
    if (this.props.pageProps.isServerSideRedirect) {
      return null
    }

    const hasError = this.props?.pageProps?.hasError
    const errorEventId = this.state?.errorEventId
    if (hasError) {
      return <SentryErrorReportingPage errorEventId={errorEventId} />
    }
    const Component = this.props?.Component
    const pageProps = this.props?.pageProps
    const apolloClient = this.props?.apolloClient
    const router = this.props?.router

    const stripeCoupon = router.query.coupon
    if (stripeCoupon) {
      Cookies.set('coupon', stripeCoupon)
    }

    return (
      <ApolloProvider client={apolloClient}>
        <CareGuideCoreApplication Component={Component} pageProps={pageProps} />
      </ApolloProvider>
    )
  }
}

export default withApolloClient(MyApp)
